import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Ellipsis, Search, X } from "lucide-react";
import { ModalShell } from "../primitives/index.js";
import { Badge, Button, Chip, Skeleton } from "../atoms/index.js";
import {
  Alert,
  EmptyState,
  ToggleGroup,
  asyncStatus,
  type ToggleGroupOption,
} from "../molecules/index.js";
import { STATE_RING } from "../lib/state-ring.js";
import { cn } from "../lib/utils.js";
import type {
  SearchResultItem,
  SearchScope,
  SearchSection,
} from "./search.types.js";

export interface UniversalSearchDialogProps {
  open: boolean;
  onClose: () => void;
  sections: SearchSection[];
  onSelect: (item: SearchResultItem) => void;
  /** Reports the current text so data-owning hosts can run remote search. */
  onQueryChange?: (query: string) => void;
  emptyQuerySections?: SearchSection[];
  placeholder?: string;
  /** When true, renders the dialog inline (no fixed overlay) so it can be embedded e.g. in the Catalog. */
  inline?: boolean;
  /**
   * Available scopes for the "Find within" chip row. When provided, a chip
   * row is rendered under the search input and selecting a chip filters
   * sections by their `scopeIds`.
   */
  scopes?: SearchScope[];
  /** Controlled active scope id. Pass `null` for "All". */
  activeScopeId?: string | null;
  /** Notified whenever the active scope changes. */
  onScopeChange?: (scopeId: string | null) => void;
  /** Number of scope chips shown inline before collapsing to an overflow menu. Default 4. */
  inlineScopeCount?: number;
}

/* A section with a status or a notice shows even with no items: hiding it
   would let an empty answer stand in for a slow or failed one. */
function hasContent(section: SearchSection): boolean {
  return (
    section.items.length > 0 ||
    section.status !== undefined ||
    section.notice !== undefined
  );
}

/* Two rows in the shape of a result row (28px tile, title, meta). */
const LOADING_ROWS = ["w-2/5", "w-1/3"];

function RetryButton({ section }: { section: SearchSection }) {
  if (!section.onRetry) return null;
  return (
    <Button
      variant="ghost"
      size="xs"
      onClick={section.onRetry}
      data-search-own-enter=""
    >
      {section.retryLabel ?? "Retry"}
    </Button>
  );
}

/* A section's remote state (invariant 8), with asyncStatus's precedence:
   rows already there win, because blanking them is a worse failure than the
   one being reported (a failed refresh keeps its earlier answer). With no
   rows, loading is a named status region with the shape of what is coming
   and a failure is EmptyState's error row with a Retry. With rows, a loading
   status draws nothing and a failure is an inline Alert beside them. */
function SectionStatus({ section }: { section: SearchSection }) {
  const view = asyncStatus({
    isLoading: section.status === "loading",
    isError: section.status === "error",
    isEmpty: false,
    hasData: section.items.length > 0,
  });
  const errorTitle = section.errorTitle ?? `Couldn’t load ${section.label}`;
  if (view === "loading") {
    return (
      <div
        role="status"
        aria-label={section.loadingLabel ?? `Loading ${section.label}`}
      >
        {LOADING_ROWS.map((width) => (
          <div
            key={width}
            aria-hidden="true"
            className="flex items-center gap-3 px-4 py-2 border-s-2 border-s-transparent"
          >
            <Skeleton className="w-7 h-7 shrink-0" />
            <span className="flex-1 min-w-0 flex flex-col gap-1.5">
              <Skeleton className={cn("h-2.5", width)} />
              <Skeleton className="h-2.5 w-3/5" />
            </span>
          </div>
        ))}
      </div>
    );
  }
  if (view === "error") {
    return (
      <div className="px-4 py-1">
        <EmptyState
          variant="error"
          size="sm"
          role="alert"
          title={errorTitle}
          action={<RetryButton section={section} />}
        />
      </div>
    );
  }
  if (section.status !== "error") return null;
  return (
    <div className="px-4 pb-1">
      {/* items-center: the 24px Retry otherwise sits below the one text line. */}
      <Alert
        variant="inline"
        tone="critical"
        className="items-center"
        actionsSlot={<RetryButton section={section} />}
      >
        {errorTitle}
      </Alert>
    </div>
  );
}

function matchItem(item: SearchResultItem, q: string): boolean {
  return (
    item.title.toLowerCase().includes(q) ||
    item.meta.toLowerCase().includes(q) ||
    item.id.toLowerCase().includes(q)
  );
}

export function UniversalSearchDialog(props: UniversalSearchDialogProps) {
  /* Mounted per open so every state hook re-initialises fresh — this is what
     resets query/highlight/scope between opens (no reset-in-effect needed). */
  if (!props.open) return null;
  return <SearchDialogContent {...props} />;
}

function SearchDialogContent({
  open,
  onClose,
  sections,
  onSelect,
  onQueryChange,
  emptyQuerySections,
  placeholder = "Search…",
  inline = false,
  scopes,
  activeScopeId,
  onScopeChange,
  inlineScopeCount = 4,
}: UniversalSearchDialogProps) {
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [internalScope, setInternalScope] = useState<string | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);
  const overflowMenuRef = useRef<HTMLDivElement>(null);
  const scopeBarRef = useRef<HTMLDivElement>(null);
  /* The overflow scope menu portals OUTSIDE the modal panel — without this,
     pressing it would count as an outside-press and close the dialog. */
  const modalInsideRefs = useMemo(() => [overflowMenuRef], []);
  const [overflowMenuPos, setOverflowMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const isControlledScope = activeScopeId !== undefined;
  const activeScope = isControlledScope
    ? (activeScopeId ?? null)
    : internalScope;

  const setActiveScope = useCallback(
    (next: string | null) => {
      if (!isControlledScope) setInternalScope(next);
      onScopeChange?.(next);
    },
    [isControlledScope, onScopeChange],
  );

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    queueMicrotask(() => inputRef.current?.focus());
    return () => {
      if (previousFocus && typeof previousFocus.focus === "function")
        previousFocus.focus();
    };
  }, []);

  const scopeFilter = useCallback(
    (s: SearchSection) => {
      if (!activeScope) return true;
      return s.scopeIds?.includes(activeScope) ?? false;
    },
    [activeScope],
  );

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const src = emptyQuerySections ?? sections;
      return src
        .filter(scopeFilter)
        .map((s) => ({ ...s, items: s.items }))
        .filter(hasContent);
    }
    return sections
      .filter(scopeFilter)
      .map((s) => ({ ...s, items: s.items.filter((it) => matchItem(it, q)) }))
      .filter(hasContent);
  }, [query, sections, emptyQuerySections, scopeFilter]);

  const flatResults = useMemo(() => grouped.flatMap((s) => s.items), [grouped]);

  /* Derived, not synced via effect: when results shrink under the stored
     highlight, clamp at read time. */
  const activeIndex = Math.min(highlight, Math.max(0, flatResults.length - 1));

  const activate = (item: SearchResultItem) => {
    onSelect(item);
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (overflowOpen) {
          setOverflowOpen(false);
          return;
        }
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((i) =>
          flatResults.length === 0
            ? 0
            : (Math.min(i, flatResults.length - 1) + 1) % flatResults.length,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((i) =>
          flatResults.length === 0
            ? 0
            : (Math.min(i, flatResults.length - 1) - 1 + flatResults.length) %
              flatResults.length,
        );
      } else if (e.key === "Enter") {
        // Don't hijack Enter when focus is in the scope row (a scope option or
        // More scopes) — those controls manage their own activation.
        // Nor on a control in the results that has its own (Retry).
        const target = e.target as Node | null;
        if (target && scopeBarRef.current?.contains(target)) return;
        if (
          target instanceof Element &&
          target.closest("[data-search-own-enter]")
        )
          return;
        const r = flatResults[activeIndex];
        if (r) {
          e.preventDefault();
          activate(r);
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flatResults, highlight, overflowOpen]);

  // Close overflow popover on outside click
  useEffect(() => {
    if (!overflowOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (overflowRef.current?.contains(t)) return;
      if (overflowMenuRef.current?.contains(t)) return;
      setOverflowOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [overflowOpen]);

  // Position the portaled overflow menu anchored to the trigger. No reset on
  // close: the menu unmounts with overflowOpen, and on reopen this layout
  // effect recomputes the stale position before paint.
  useLayoutEffect(() => {
    if (!overflowOpen) return;
    const compute = () => {
      const trigger = overflowRef.current;
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      const cw = overflowMenuRef.current?.offsetWidth ?? 160;
      let left = r.right - cw;
      const maxLeft = window.innerWidth - cw - 8;
      if (left > maxLeft) left = maxLeft;
      if (left < 8) left = 8;
      setOverflowMenuPos({ top: r.bottom + 4, left });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [overflowOpen]);

  const visibleScopes = scopes?.slice(0, inlineScopeCount) ?? [];
  const overflowScopes = scopes?.slice(inlineScopeCount) ?? [];
  const hasOverflow = overflowScopes.length > 0;
  const activeScopeObj = scopes?.find((s) => s.id === activeScope) ?? null;
  const activeIsOverflow =
    !!activeScopeObj && overflowScopes.some((s) => s.id === activeScopeObj.id);

  /* One scope as a ToggleGroup option (OK-95). A pressed option takes the
     teal selection, so its glyph follows the button's ink; at rest the glyph
     keeps the scope's own accent. */
  const scopeOption = (scope: SearchScope): ToggleGroupOption<string> => {
    const ScopeIcon = scope.icon;
    const active = activeScope === scope.id;
    return ScopeIcon
      ? {
          value: scope.id,
          label: scope.label,
          icon: (
            <ScopeIcon
              className={cn(
                "w-3 h-3",
                !active && (scope.accent ?? "text-pb-text-muted"),
              )}
            />
          ),
        }
      : { value: scope.id, label: scope.label };
  };

  /* DS keep (PR 3): the search card is its own non-modal/modal surface
     inside ModalShell (inline or overlay); Modal's chrome does not fit it. */
  const card = (
    <div
      ref={cardRef}
      role="dialog"
      aria-modal={inline ? undefined : "true"}
      aria-label="Universal search"
      className={
        inline
          ? "w-full max-w-160 bg-pb-shell border border-pb-border-strong rounded-xl shadow-lg overflow-hidden pb-hairline"
          : "mt-32 w-160 max-w-[92vw] bg-pb-shell border border-pb-border-strong rounded-xl shadow-2xl overflow-hidden pb-hairline"
      }
    >
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-pb-border">
        <Search className="w-4 h-4 text-pb-text-muted shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            const next = e.target.value;
            setQuery(next);
            onQueryChange?.(next);
            setHighlight(0);
          }}
          placeholder={
            activeScopeObj ? `Search in ${activeScopeObj.label}…` : placeholder
          }
          className="flex-1 bg-transparent border-0 outline-none text-pb-text placeholder:text-pb-text-muted text-sm py-1"
        />
        {/* The active scope is a removable Chip (OK-95). */}
        {activeScopeObj ? (
          <Chip
            label={activeScopeObj.label}
            onRemove={() => setActiveScope(null)}
            removeLabel={`Clear ${activeScopeObj.label} scope`}
            aria-live="polite"
            className="shrink-0"
          />
        ) : null}
        {/* Search is federated across owner services but remains an early
            surface without relevance ranking. Keep the maturity label until
            ranking and richer result states land.
            Hardcoded rather than exposed as a slot on purpose: a maturity label
            with one consumer is presentation, not a configuration axis. */}
        <Badge variant="outline" tone="neutral" className="shrink-0">
          Beta
        </Badge>
        <kbd className="text-dense-2 font-mono uppercase tracking-wider text-pb-text-muted bg-pb-card border border-pb-border rounded px-1.5 py-0.5">
          Esc
        </kbd>
        <button
          onClick={onClose}
          aria-label="Close search"
          className="ms-1 w-6 h-6 flex items-center justify-center rounded text-pb-text-muted hover:text-pb-text hover:pb-overlay-paint transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {scopes && scopes.length > 0 ? (
        <div
          ref={scopeBarRef}
          role="toolbar"
          aria-label="Find within"
          className="flex items-center gap-2 px-3 py-2 border-b border-pb-border overflow-x-auto"
        >
          <span className="shrink-0 text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted pe-0.5">
            Find within
          </span>
          <ToggleGroup
            aria-label="Scopes"
            size="xs"
            clearable
            value={activeScope}
            onChange={setActiveScope}
            options={[
              ...visibleScopes,
              ...(activeIsOverflow && activeScopeObj ? [activeScopeObj] : []),
            ].map(scopeOption)}
          />
          {hasOverflow ? (
            <div ref={overflowRef} className="relative">
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={overflowOpen}
                aria-label="More scopes"
                onClick={() => setOverflowOpen((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOverflowOpen((v) => !v);
                  }
                }}
                className="shrink-0 inline-flex items-center gap-1 rounded-full border border-pb-control-border bg-pb-card px-2.5 py-1 text-xs text-pb-text-secondary hover:pb-overlay-paint hover:text-pb-text focus:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal/60"
              >
                <Ellipsis className="w-3.5 h-3.5" />
              </button>
              {overflowOpen && typeof document !== "undefined"
                ? createPortal(
                    <div
                      ref={overflowMenuRef}
                      role="menu"
                      style={{
                        position: "fixed",
                        top: overflowMenuPos?.top ?? -9999,
                        left: overflowMenuPos?.left ?? -9999,
                        visibility: overflowMenuPos ? "visible" : "hidden",
                      }}
                      className="z-popover min-w-40 bg-pb-shell border border-pb-border-strong rounded-md shadow-lg py-1"
                    >
                      {overflowScopes.map((s) => {
                        const active = activeScope === s.id;
                        const ScopeIcon = s.icon;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            role="menuitemradio"
                            aria-checked={active}
                            onClick={() => {
                              setActiveScope(active ? null : s.id);
                              setOverflowOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-start transition-colors ${
                              active
                                ? `bg-pb-card-elevated text-pb-text ${STATE_RING.teal}`
                                : "text-pb-text-secondary hover:pb-overlay-paint hover:text-pb-text"
                            }`}
                          >
                            {ScopeIcon ? (
                              <ScopeIcon
                                className={`w-3.5 h-3.5 ${
                                  active
                                    ? "text-pb-teal-text"
                                    : (s.accent ?? "text-pb-text-muted")
                                }`}
                              />
                            ) : null}
                            <span className="flex-1">{s.label}</span>
                            {active ? (
                              <span className="w-1.5 h-1.5 rounded-full bg-pb-teal" />
                            ) : null}
                          </button>
                        );
                      })}
                      {activeScope ? (
                        <>
                          <div className="my-1 border-t border-pb-border" />
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setActiveScope(null);
                              setOverflowOpen(false);
                            }}
                            className="w-full text-start px-3 py-1.5 text-xs text-pb-text-secondary hover:pb-overlay-paint hover:text-pb-text"
                          >
                            Clear scope
                          </button>
                        </>
                      ) : null}
                    </div>,
                    document.body,
                  )
                : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="max-h-[60vh] overflow-y-auto">
        {grouped.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Search className="w-6 h-6 text-pb-text-muted mx-auto mb-2 opacity-60" />
            <div className="text-sm text-pb-text-secondary">
              {query ? (
                <>
                  No matches for &lsquo;{query}&rsquo;
                  {activeScopeObj ? ` in ${activeScopeObj.label}` : ""}
                </>
              ) : activeScopeObj ? (
                `No ${activeScopeObj.label.toLowerCase()} to show yet`
              ) : (
                "No results"
              )}
            </div>
            {activeScopeObj ? (
              <button
                type="button"
                onClick={() => setActiveScope(null)}
                className="mt-3 text-dense-1 text-pb-teal-text hover:underline"
              >
                Clear scope
              </button>
            ) : null}
          </div>
        ) : (
          grouped.map((section) => (
            <div key={section.id}>
              <div className="px-4 pt-3 pb-1 text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted">
                {section.label}
              </div>
              {section.notice ? (
                <div className="px-4 pb-1">
                  <Alert variant="inline" tone="watch" role="status">
                    {section.notice}
                  </Alert>
                </div>
              ) : null}
              {section.status ? <SectionStatus section={section} /> : null}
              <div className="pb-1">
                {section.items.map((r) => {
                  const flatIndex = flatResults.indexOf(r);
                  const active = flatIndex === activeIndex;
                  const Icon = r.icon;
                  return (
                    <button
                      key={r.id}
                      onMouseEnter={() => setHighlight(flatIndex)}
                      onClick={() => activate(r)}
                      className={`w-full text-start flex items-center gap-3 px-4 py-2 border-s-2 transition-colors ${
                        active
                          ? "bg-pb-card-elevated border-s-pb-teal"
                          : "border-s-transparent hover:pb-overlay-paint"
                      }`}
                    >
                      <div
                        className={`w-7 h-7 shrink-0 rounded-md bg-pb-card border border-pb-border flex items-center justify-center ${
                          r.accent ?? "text-pb-text-secondary"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-pb-text truncate">
                          {r.title}
                        </div>
                        <div className="text-dense-1 text-pb-text-muted truncate">
                          {r.meta}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="px-3 py-2 border-t border-pb-border flex items-center justify-between text-dense-1 text-pb-text-muted">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <kbd className="font-mono bg-pb-card border border-pb-border rounded px-1">
              ↑
            </kbd>
            <kbd className="font-mono bg-pb-card border border-pb-border rounded px-1">
              ↓
            </kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="font-mono bg-pb-card border border-pb-border rounded px-1">
              ↵
            </kbd>
            open
          </span>
        </div>
        <span>{flatResults.length} results</span>
      </div>
    </div>
  );

  if (inline) return card;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      panelSemantics={false}
      dismissOnEscape={false}
      extraInsideRefs={modalInsideRefs}
      containerClassName="items-start justify-center"
    >
      {card}
    </ModalShell>
  );
}
